import { Link, useParams } from 'react-router-dom'
import { useAuth } from 'react-oidc-context'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useCommunity,
  useJoinCommunity,
  useLeaveCommunity,
} from '@/features/communities/use-communities'

export const CommunityAboutPage = () => {
  const { slug } = useParams<{ slug: string }>()
  const { data: community, isLoading } = useCommunity(slug)
  const auth = useAuth()
  const join = useJoinCommunity(slug ?? '')
  const leave = useLeaveCommunity(slug ?? '')

  if (!slug) return null
  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-3 px-4 py-6">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }
  if (!community) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12">
        <Alert>
          <AlertTitle>Community not found</AlertTitle>
          <AlertDescription>
            <Link to="/c" className="underline underline-offset-2">
              Browse all communities
            </Link>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const isMember = community.viewerMembership !== null
  const isOwner = community.viewerMembership === 'OWNER'

  const onJoin = async () => {
    try {
      await join.mutateAsync()
      toast.success(`Joined /c/${community.slug}`)
    } catch {
      toast.error('Failed to join')
    }
  }
  const onLeave = async () => {
    try {
      await leave.mutateAsync()
      toast.success(`Left /c/${community.slug}`)
    } catch {
      toast.error('Failed to leave')
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-6">
      <Button variant="ghost" size="sm" asChild className="gap-2 px-2">
        <Link to={`/c/${community.slug}`}>
          <ArrowLeft className="h-4 w-4" /> Back to /c/{community.slug}
        </Link>
      </Button>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{community.name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {community.description && (
            <p className="whitespace-pre-wrap text-sm">{community.description}</p>
          )}
          <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-3">
            <div>
              <div className="text-xs uppercase tracking-wide">Members</div>
              <div className="text-base text-foreground">{community.memberCount}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide">Projects</div>
              <div className="text-base text-foreground">{community.projectCount}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide">Owner</div>
              <div className="text-base text-foreground">
                <Link to={`/u/${community.owner.handle}`} className="hover:underline">
                  @{community.owner.handle}
                </Link>
              </div>
            </div>
          </div>
          {auth.isAuthenticated && !isOwner && (
            <div>
              {isMember ? (
                <Button variant="outline" onClick={onLeave} disabled={leave.isPending}>
                  Leave
                </Button>
              ) : (
                <Button onClick={onJoin} disabled={join.isPending}>
                  Join
                </Button>
              )}
            </div>
          )}
          {isOwner && (
            <p className="text-xs text-muted-foreground">
              You own this community. Owners cannot leave their own community in v1.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
